// File: src/components/Admin/ActivityLog.tsx
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  CircularProgress,
  Alert,
  TextField,
  InputAdornment,
  IconButton,
  Tooltip,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
} from '@mui/material';
import {
  Search as SearchIcon,
  Refresh as RefreshIcon,
  Clear as ClearIcon,
  CheckCircle as SuccessIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  InfoOutlined as InfoIcon,
} from '@mui/icons-material';
import { useAuth } from '../../contexts/AuthContext';
import api from '../../services/api';
import { useCustomerFilter } from '../../hooks/useCustomerFilter';

interface ActivityLogEntry {
  id: number;
  user_id: number;
  customer_id: number | null;
  username: string;
  action: string;
  description: string;
  ip_address: string;
  status: 'success' | 'error' | 'warning';
  created_at: string;
  metadata?: any;
  action_summary?: string;
}

const ActivityLog: React.FC = () => {
  const { member } = useAuth();
  const customerFilter = useCustomerFilter();
  const [loading, setLoading] = useState(true);
  const [logs, setLogs] = useState<ActivityLogEntry[]>([]);
  const [filteredLogs, setFilteredLogs] = useState<ActivityLogEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(25);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterAction, setFilterAction] = useState('all');
  const [filterStatus, setFilterStatus] = useState('all');
  const [detailsDialog, setDetailsDialog] = useState<ActivityLogEntry | null>(null);
  
  // Check for login-as-customer token and session data
  const urlParams = new URLSearchParams(window.location.search);
  const hasLoginToken = urlParams.has('lat');
  const hasSessionData = sessionStorage.getItem('loginAsCustomerId') && sessionStorage.getItem('loginAsCustomerName');
  
  // Initialize waiting state based on token presence
  const [waitingForLoginAs] = useState(() => {
    const shouldWait = hasLoginToken && !hasSessionData;
    if (shouldWait) {
      console.log('[ActivityLog] Initializing with waiting=true due to login token');
    }
    return shouldWait;
  });

  const fetchActivityLogs = async () => {
    // Safety check - don't fetch if we're waiting for login-as data
    if (waitingForLoginAs) {
      console.log('[ActivityLog] Skipping fetch - waiting for login-as data');
      return;
    }
    
    // Verify customer context matches if in login-as mode
    const sessionCustomerId = sessionStorage.getItem('loginAsCustomerId');
    if (sessionCustomerId && customerFilter.customerId && 
        sessionCustomerId !== String(customerFilter.customerId)) {
      console.log('[ActivityLog] Customer mismatch - waiting for correct context');
      setLoading(false);
      return;
    }
    
    try {
      setLoading(true);
      const params: any = {};
      
      // Add customer filter for master admins and reseller admins
      if (customerFilter.isFiltered && (member?.memberType === 'master_admin' || member?.memberType === 'reseller_admin')) {
        params.customerId = customerFilter.customerId;
      }
      
      console.log('[ActivityLog] Fetching activity logs with params:', params);
      const response = await api.get('/endpoints/activity_logs', { params });
      if (response.data.success) {
        // Handle both possible response formats
        const logs = response.data.data?.logs || response.data.logs || response.data.data || [];
        setLogs(Array.isArray(logs) ? logs : []);
      }
      setError(null);
    } catch (err: any) {
      setError(err.response?.data?.message || 'Failed to load activity logs');
      console.error('Activity logs error:', err);
    } finally {
      setLoading(false);
    }
  };

  const filterLogs = useCallback(() => {
    let filtered = [...logs];

    // Search filter
    if (searchTerm) {
      filtered = filtered.filter(log =>
        log.username.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.action.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.description.toLowerCase().includes(searchTerm.toLowerCase()) ||
        log.ip_address.includes(searchTerm)
      );
    }

    // Action filter
    if (filterAction !== 'all') {
      filtered = filtered.filter(log => log.action === filterAction);
    }

    // Status filter
    if (filterStatus !== 'all') {
      filtered = filtered.filter(log => log.status === filterStatus);
    }

    setFilteredLogs(filtered);
    setPage(0); // Reset to first page when filtering
  }, [logs, searchTerm, filterAction, filterStatus]);

  useEffect(() => {
    if (member && ['master_admin', 'reseller_admin'].includes(member.memberType) && !waitingForLoginAs) {
      // Add small delay to ensure session storage is ready
      const timer = setTimeout(() => {
        fetchActivityLogs();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [member, waitingForLoginAs]);
  
  // Listen for login-as-customer changes
  useEffect(() => {
    const handleLoginAsChange = () => {
      console.log('[ActivityLog] Login-as-customer changed, refreshing data');
      if (member && ['master_admin', 'reseller_admin'].includes(member.memberType)) {
        fetchActivityLogs();
      }
    };
    
    window.addEventListener('loginAsCustomerChanged', handleLoginAsChange);
    return () => window.removeEventListener('loginAsCustomerChanged', handleLoginAsChange);
  }, [member]);
  
  // Update browser tab title with customer info
  useEffect(() => {
    if (customerFilter.isFiltered && customerFilter.customerId && customerFilter.customerName) {
      document.title = `${customerFilter.customerId} - ${customerFilter.customerName} - Activity Log`;
    } else {
      document.title = 'Activity Log - Admin Portal';
    }
  }, [customerFilter]);

  useEffect(() => {
    filterLogs();
  }, [filterLogs]);

  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };

  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };

  const getStatusIcon = (status: string): React.ReactElement | undefined => {
    switch (status) {
      case 'success':
        return <SuccessIcon fontSize="small" />;
      case 'error':
        return <ErrorIcon fontSize="small" />;
      case 'warning':
        return <WarningIcon fontSize="small" />;
      default:
        return undefined;
    }
  };

  const getStatusColor = (status: string): 'success' | 'error' | 'warning' | 'default' => {
    switch (status) {
      case 'success':
        return 'success';
      case 'error':
        return 'error';
      case 'warning':
        return 'warning';
      default:
        return 'default';
    }
  };

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString();
  };


  const getActionLabel = (action: string) => {
    // Check if it's a debug action
    if (action.startsWith('debug_')) {
      return `Debug: ${action.substring(6)}`;
    }
    // Check if it's an error action
    if (action.startsWith('error_')) {
      return `Error: ${action.substring(6)}`;
    }
    // Check if it's a warning action
    if (action.startsWith('warning_')) {
      return `Warning: ${action.substring(8)}`;
    }
    
    const actionMap: { [key: string]: string } = {
      'login': 'User Login',
      'logout': 'User Logout',
      'extension_create': 'Extension Created',
      'extension_assign': 'Extension Assignment',
      'extension_update': 'Extension Update',
      'extension_delete': 'Extension Delete',
      'extension_unassign_for_delete': 'Extension Unassigned (Pre-Delete)',
      'extension_delete_attempt': 'Extension Delete Attempt',
      'extension_delete_error': 'Extension Delete Error',
      'softphone_create': 'Softphone Created',
      'softphone_created': 'Softphone Created',
      'softphone_update': 'Softphone Updated',
      'softphone_delete': 'Softphone Deleted',
      'softphone_deleted': 'Softphone Deleted',
      'softphone_password_reset': 'Softphone Password Reset',
      'softphone_toggle': 'Softphone Toggled',
      'softphone_enabled': 'Softphone Enabled',
      'softphone_disabled': 'Softphone Disabled',
      'pin_reset': 'PIN Reset',
      'reset_voicemail_pin': 'Voicemail PIN Reset',
      'reset_softphone_pin': 'Softphone PIN Reset',
      'package_update': 'Package Update',
      'phone_number_update': 'Phone Number Update',
      'phone_number_add': 'Phone Number Added',
      'phone_number_create': 'Phone Number Created',
      'phone_number_cancellation_requested': 'Phone Number Cancellation Requested',
      'phone_number_cancellation_reversed': 'Phone Number Cancellation Reversed',
      'phone_number_scheduled_cancellation': 'Phone Number Scheduled Cancellation',
      'add_package': 'Package Created',
      'update_package': 'Package Updated',
      'toggle_package': 'Package Status Changed',
      'api_call': 'API Call',
      'system_event': 'System Event',
      'created_customer': 'Customer Created',
      'updated_customer': 'Customer Updated',
      'customer_deactivated': 'Customer Deactivated',
      'user_created': 'User Created',
      'user_updated': 'User Updated',
      'user_deleted': 'User Deleted',
      'welcome_email_sent': 'Welcome Email Sent',
      'invitation_resent': 'Invitation Resent',
      'login_as_user': 'Login As User',
      'user_shared_numbers_updated': 'User Shared Numbers Updated',
      'ring_group_create': 'Ring Group Created',
      'ring_group_update': 'Ring Group Updated',
      'ring_group_delete': 'Ring Group Deleted',
      'ring_group_member_add': 'Ring Group Member Added',
      'ring_group_member_remove': 'Ring Group Member Removed',
      'ring_group_member_reactivate': 'Ring Group Member Reactivated',
      'ivr_created': 'IVR Menu Created',
      'ivr_updated': 'IVR Menu Updated',
      'ivr_deleted': 'IVR Menu Deleted',
      'time_condition_created': 'Time Condition Created',
      'time_condition_updated': 'Time Condition Updated',
      'time_condition_deleted': 'Time Condition Deleted',
      'time_period_created': 'Time Period Created',
      'time_period_updated': 'Time Period Updated',
      'time_period_deleted': 'Time Period Deleted',
      'time_conditions_toggled': 'Time Conditions Toggled',
      'prompt_created': 'Prompt Created',
      'recording_uploaded': 'Recording Uploaded',
      'recording_updated': 'Recording Updated',
      'recording_deleted': 'Recording Deleted',
      'recording_rule_created': 'Recording Rule Created',
      'recording_rule_updated': 'Recording Rule Updated',
      'recording_rule_deleted': 'Recording Rule Deleted',
      'recording_rules_bulk_deleted': 'All Recording Rules Deleted',
      'payment_posted': 'Payment Posted',
      'payment_applied': 'Payment Applied to Invoice',
      'credit_applied': 'Credit Applied',
      'payment_refunded': 'Payment Refunded',
      'add_addon_service': 'Add-on Service Created',
      'update_addon_service': 'Add-on Service Updated',
      'delete_addon_service': 'Add-on Service Deleted',
    };
    return actionMap[action] || action;
  };

  // Get unique actions for filter
  const uniqueActions = Array.from(new Set(logs.map(log => log.action))).sort();

  if (member && !['master_admin', 'reseller_admin'].includes(member.memberType)) {
    return (
      <Alert severity="error">
        You do not have permission to view activity logs.
      </Alert>
    );
  }

  return (
    <Box>
      <Paper sx={{ p: 2, mb: 2 }}>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Typography variant="h4" component="h1">
            Activity Log
          </Typography>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={fetchActivityLogs}
            disabled={loading}
          >
            Refresh
          </Button>
        </Box>

        {/* Filters */}
        <Box display="flex" gap={2} mb={3} flexWrap="wrap">
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            sx={{ minWidth: 250 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon />
                </InputAdornment>
              ),
              endAdornment: searchTerm && (
                <InputAdornment position="end">
                  <IconButton size="small" onClick={() => setSearchTerm('')}>
                    <ClearIcon />
                  </IconButton>
                </InputAdornment>
              ),
            }}
          />

          <FormControl size="small" sx={{ minWidth: 200 }}>
            <InputLabel>Action</InputLabel>
            <Select
              value={filterAction}
              onChange={(e) => setFilterAction(e.target.value)}
              label="Action"
            >
              <MenuItem value="all">All Actions</MenuItem>
              {uniqueActions.map(action => (
                <MenuItem key={action} value={action}>
                  {getActionLabel(action)}
                </MenuItem>
              ))}
            </Select>
          </FormControl>

          <FormControl size="small" sx={{ minWidth: 150 }}>
            <InputLabel>Status</InputLabel>
            <Select
              value={filterStatus}
              onChange={(e) => setFilterStatus(e.target.value)}
              label="Status"
            >
              <MenuItem value="all">All Statuses</MenuItem>
              <MenuItem value="success">Success</MenuItem>
              <MenuItem value="error">Error</MenuItem>
              <MenuItem value="warning">Warning</MenuItem>
            </Select>
          </FormControl>
        </Box>

        {/* Error/Success Messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
            {error}
          </Alert>
        )}

        {/* Activity Log Table */}
        {loading ? (
          <Box display="flex" justifyContent="center" p={4}>
            <CircularProgress />
          </Box>
        ) : (
          <>
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date/Time</TableCell>
                    <TableCell>CustID</TableCell>
                    <TableCell>User</TableCell>
                    <TableCell>Action</TableCell>
                    <TableCell>Description</TableCell>
                    <TableCell>IP Address</TableCell>
                    <TableCell align="center">Status</TableCell>
                    <TableCell align="center">Details</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {filteredLogs
                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                    .map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <Typography variant="body2">
                            {formatDate(log.created_at)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.customer_id || '-'}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {getActionLabel(log.action)}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Tooltip title={log.description}>
                            <Typography
                              variant="body2"
                              sx={{
                                maxWidth: 300,
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {log.description}
                            </Typography>
                          </Tooltip>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">
                            {log.ip_address}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Chip
                            icon={getStatusIcon(log.status)}
                            label={log.status}
                            size="small"
                            color={getStatusColor(log.status)}
                          />
                        </TableCell>
                        <TableCell align="center">
                          {log.metadata && (
                            <Tooltip title="View Details">
                              <IconButton
                                size="small"
                                onClick={() => setDetailsDialog(log)}
                              >
                                <InfoIcon />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    ))}
                  {filteredLogs.length === 0 && (
                    <TableRow>
                      <TableCell colSpan={8} align="center">
                        <Typography variant="body2" color="textSecondary">
                          No activity logs found
                        </Typography>
                      </TableCell>
                    </TableRow>
                  )}
                </TableBody>
              </Table>
            </TableContainer>

            <TablePagination
              rowsPerPageOptions={[10, 25, 50, 100]}
              component="div"
              count={filteredLogs.length}
              rowsPerPage={rowsPerPage}
              page={page}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
            />
          </>
        )}
      </Paper>

      {/* Details Dialog */}
      <Dialog
        open={Boolean(detailsDialog)}
        onClose={() => setDetailsDialog(null)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>
          Activity Details
        </DialogTitle>
        <DialogContent dividers>
          {detailsDialog && (
            <>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Action:</strong> {getActionLabel(detailsDialog.action)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>User:</strong> {detailsDialog.username}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Time:</strong> {formatDate(detailsDialog.created_at)}
              </Typography>
              <Typography variant="subtitle2" gutterBottom>
                <strong>Description:</strong> {detailsDialog.description}
              </Typography>
              
              {detailsDialog.action_summary && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    What Changed
                  </Typography>
                  <Box sx={{ bgcolor: 'background.paper', p: 2, borderRadius: 1, border: '1px solid', borderColor: 'divider' }}>
                    <Typography variant="body1" style={{ whiteSpace: 'pre-line' }}>
                      {detailsDialog.action_summary.split(' | ').join('\n')}
                    </Typography>
                  </Box>
                </>
              )}
              
              {detailsDialog.metadata && process.env.NODE_ENV === 'development' && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="h6" gutterBottom>
                    Technical Details (Dev Only)
                  </Typography>
                  <Box sx={{ bgcolor: 'grey.100', p: 2, borderRadius: 1, maxHeight: 300, overflow: 'auto' }}>
                    <pre style={{ margin: 0, fontSize: '0.875rem' }}>
                      {JSON.stringify(detailsDialog.metadata, null, 2)}
                    </pre>
                  </Box>
                </>
              )}
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialog(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ActivityLog;